import React, { useState, useEffect } from "react";
import { API_BASE_URL } from "../config";
import { useLocation, useNavigate } from "react-router-dom";
import "./FeedbackPage.css";

const FeedbackPage = () => {
  const [rating, setRating] = useState(0);
  const [feedbackText, setFeedbackText] = useState("");
  const [submittingFeedback, setSubmittingFeedback] = useState(false);
  const [doctorId, setDoctorId] = useState(null);
  const [fromCall, setFromCall] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Check if this is a reload (no need to reload again)
    const hasReloaded = sessionStorage.getItem("feedbackPageReloaded");

    // Get data from location state
    if (location.state && location.state.doctorId) {
      setDoctorId(location.state.doctorId);
      setFromCall(location.state.callFrom || "doctorProfile");

      // Only reload if we haven't reloaded before
      if (!hasReloaded) {
        sessionStorage.setItem("feedbackPageReloaded", "true");
        window.location.reload();
      }
    } else {
      // If no state, redirect to home
      window.location.href = `/`;
    }
  }, []);

  // Clean up the reload flag when component unmounts
  useEffect(() => {
    return () => {
      sessionStorage.removeItem("feedbackPageReloaded");
    };
  }, []);
  const handleSubmitFeedback = async () => {
    if (rating === 0) {
      setErrorMessage("Please provide a rating before submitting feedback.");
      return;
    }

    setSubmittingFeedback(true);
    setErrorMessage(""); // Clear any previous error messages
    try {
      const session = JSON.parse(sessionStorage.getItem("user"));
      const patientId = session?.payLoad?.user?.Id;

      if (!doctorId || !patientId) {
        throw new Error("Missing doctor or patient information");
      }

      const feedbackData = {
        DoctorId: doctorId,
        PatientId: patientId,
        RatingPoints: rating.toString(),
        FeedbackText: feedbackText,
      };

      const response = await fetch(
        `${API_BASE_URL}/feedback/save`,
        {
          method: "POST",
          body: JSON.stringify(feedbackData),
        }
      );

      if (response.ok) {
        handleSkipFeedback();
      } else {
        throw new Error("Failed to submit feedback");
      }
    } catch (error) {
      console.error("Error submitting feedback:", error);
      setErrorMessage(
        error.message || "Failed to submit feedback. Please try again."
      );
    } finally {
      setSubmittingFeedback(false);
    }
  };

  const handleSkipFeedback = () => {
    // Redirect back to doctor profile if doctorId exists, otherwise go to home
    if (doctorId) {
      if (fromCall === "doctorProfile") {
        navigate(`/doctor-profile/${doctorId}`);
      } else if (fromCall === "fromInstant") {
        navigate(`/`);
      } else if (fromCall === "fromAppointment") {
        navigate(`/appointment`);
      }
    } else {
      navigate("/");
    }
  };

  const getRatingText = (rating) => {
    switch (rating) {
      case 1:
        return "Poor";
      case 2:
        return "Fair";
      case 3:
        return "Good";
      case 4:
        return "Very Good";
      case 5:
        return "Excellent";
      default:
        return "";
    }
  };

  return (
    <div className="feedback-page">
      <div className="feedback-container">
        <div className="feedback-header">
           <h4>Share your feedback</h4>
           <span>(optional)</span>
        </div>

        <div className="feedback-content">
          {/* Star Rating */}
          <div className="star-rating-section">
            <h4>Rate your experience</h4>
            <div className="star-rating">
              {[1, 2, 3, 4, 5].map((star) => (
                <span
                  key={star}
                  onClick={() => {
                    setRating(star);
                    setErrorMessage(""); // Clear error when rating is selected
                  }}
                  className={`star ${star <= rating ? "filled" : "empty"}`}
                >
                  ★
                </span>
              ))}
            </div>

            {rating > 0 && (
              <p className="rating-text">{getRatingText(rating)}</p>
            )}
          </div>

          {/* Feedback Text */}
          <div className="feedback-text-section">
            <textarea
              value={feedbackText}
              onChange={(e) => {
                setFeedbackText(e.target.value);
                if (errorMessage) setErrorMessage(""); // Clear error when user starts typing
              }}
              className="feedback-textarea"
              rows="5"
              placeholder="Tell us about your experience with the doctor..."
              maxLength={500}
            />
            <small className="character-count">
              {feedbackText.length}/500 characters
            </small>{" "}
          </div>

          {/* Error Message */}
          {errorMessage && (
            <div className="error-message">
              <p>{errorMessage}</p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="feedback-actions">
            <button
              className="btn-skip"
              onClick={handleSkipFeedback}
              disabled={submittingFeedback}
            >
              Skip
            </button>
            <button
              className="btn-submit"
              onClick={handleSubmitFeedback}
              disabled={submittingFeedback || rating === 0}
            >
              {submittingFeedback ? (
                <>
                  <span className="spinner"></span>
                  Submitting...
                </>
              ) : (
                "Submit Feedback"
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FeedbackPage;
